import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

import { supabase } from "@/integrations/supabase/client";
import { getAnalyticsOverview } from "@/lib/analytics.functions";
import { getSeedAnalyticsCount, deleteSeedAnalytics } from "@/lib/admin.functions";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PeriodFilter, usePeriodFilter } from "@/components/admin/PeriodFilter";
import { AdminGate } from "@/components/admin/AdminGate";
import { AdsInvestimentoCard } from "@/components/admin/AdsInvestimentoCard";

export const Route = createFileRoute("/admin/dashboard")({
  head: () => ({
    meta: [
      { title: "Admin — Dashboard" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => <AdminGate route="/admin/dashboard"><DashboardPage /></AdminGate>,
});

function DashboardPage() {
  const navigate = useNavigate();
  const [authReady, setAuthReady] = useState(false);
  const period = usePeriodFilter("today");

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) navigate({ to: "/admin" });
      else setAuthReady(true);
    });
  }, [navigate]);

  const { fromIso, toIso } = useMemo(() => {
    const to = period.range.to ?? new Date().toISOString();
    const from = period.range.from ?? new Date(2020, 0, 1).toISOString();
    return { fromIso: from, toIso: to };
  }, [period.range]);

  const fetchOverview = useServerFn(getAnalyticsOverview);
  const fetchSeedCount = useServerFn(getSeedAnalyticsCount);
  const deleteSeedFn = useServerFn(deleteSeedAnalytics);
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["analytics-overview", fromIso, toIso],
    queryFn: () => fetchOverview({ data: { fromIso, toIso } }),
    enabled: authReady,
  });
  const { data: seed, refetch: refetchSeed } = useQuery({
    queryKey: ["seed-count"],
    queryFn: () => fetchSeedCount(),
    enabled: authReady,
  });

  const views = data?.views ?? [];
  const clicks = data?.clicks ?? [];
  const leadsAll = data?.leads ?? [];
  const salesAll = data?.sales ?? [];

  // KPIs base
  const totalViews = views.length;
  const uniqueSessions = new Set(views.map((v) => v.session_id)).size;
  const totalClicks = clicks.length;
  const totalLeads = leadsAll.length;
  const leadsHot = leadsAll.filter((l) => l.classificacao === "quente").length;
  const totalSales = salesAll.length;
  const totalRevenue = salesAll.reduce((s, r) => s + Number(r.revenue_value ?? r.gross_value ?? 0), 0);

  // Mapear origem/landing_path do lead → path da LP
  const pathFromLead = (l: { landing_path: string | null; origem: string | null }): string | null => {
    if (l.landing_path && l.landing_path.startsWith("/")) return l.landing_path;
    const o = l.origem ?? "";
    const aula = o.match(/^aulas?-exp(?:erimentais)?-([a-z0-9-]+?)(?:-parcial)?$/i);
    if (aula) return `/aulas-experimentais-${aula[1]}`;
    if (o.startsWith("revista")) return "/revista";
    if (o.startsWith("ementa-site-")) return `/ementa/${o.replace(/^ementa-site-/, "").replace(/-parcial$/, "")}`;
    return null;
  };

  // Mapear venda → path da LP (via lead atribuído ou utm_content)
  const leadIndex = useMemo(() => {
    const m = new Map<string, { landing_path: string | null; origem: string | null }>();
    for (const l of leadsAll) m.set(l.id, { landing_path: l.landing_path, origem: l.origem });
    return m;
  }, [leadsAll]);
  const pathFromSale = (s: {
    attributed_lead_id: string | null;
    utm_content: string | null;
    utm_campaign: string | null;
  }): string | null => {
    if (s.attributed_lead_id) {
      const l = leadIndex.get(s.attributed_lead_id);
      if (l) {
        const p = pathFromLead(l);
        if (p) return p;
      }
    }
    if (s.utm_content && s.utm_content.startsWith("/")) return s.utm_content;
    return null;
  };

  // Per-page aggregation (com leads + vendas + faturamento)
  const perPage = useMemo(() => {
    type Row = {
      path: string;
      views: number;
      sessions: Set<string>;
      clicks: number;
      leads: number;
      leadsHot: number;
      sales: number;
      revenue: number;
      buttons: Map<string, { label: string; destination: string | null; count: number }>;
    };
    const ensure = (path: string, map: Map<string, Row>): Row => {
      const r = map.get(path) ?? {
        path,
        views: 0,
        sessions: new Set<string>(),
        clicks: 0,
        leads: 0,
        leadsHot: 0,
        sales: 0,
        revenue: 0,
        buttons: new Map(),
      };
      map.set(path, r);
      return r;
    };
    const map = new Map<string, Row>();
    for (const v of views) {
      const m = ensure(v.path, map);
      m.views += 1;
      m.sessions.add(v.session_id);
    }
    for (const c of clicks) {
      const m = ensure(c.path, map);
      m.clicks += 1;
      const key = c.button_id ?? c.button_label ?? "?";
      const b = m.buttons.get(key) ?? {
        label: c.button_label ?? c.button_id ?? "—",
        destination: c.destination,
        count: 0,
      };
      b.count += 1;
      m.buttons.set(key, b);
    }
    for (const l of leadsAll) {
      const p = pathFromLead(l);
      if (!p) continue;
      const m = ensure(p, map);
      m.leads += 1;
      if (l.classificacao === "quente") m.leadsHot += 1;
    }
    for (const s of salesAll) {
      const p = pathFromSale(s);
      if (!p) continue;
      const m = ensure(p, map);
      m.sales += 1;
      m.revenue += Number(s.revenue_value ?? s.gross_value ?? 0);
    }
    return Array.from(map.values()).sort((a, b) => b.sessions.size - a.sessions.size);
  }, [views, clicks, leadsAll, salesAll, leadIndex]);

  // UTM sources aggregation
  const utmRows = useMemo(() => {
    const map = new Map<string, { key: string; source: string; medium: string; campaign: string; sessions: Set<string> }>();
    for (const v of views) {
      if (!v.utm_source && !v.utm_medium && !v.utm_campaign) continue;
      const source = v.utm_source ?? "—";
      const medium = v.utm_medium ?? "—";
      const campaign = v.utm_campaign ?? "—";
      const key = `${source}|${medium}|${campaign}`;
      const r = map.get(key) ?? { key, source, medium, campaign, sessions: new Set<string>() };
      r.sessions.add(v.session_id);
      map.set(key, r);
    }
    return Array.from(map.values()).sort((a, b) => b.sessions.size - a.sessions.size);
  }, [views]);

  // Per-day series for chart — inclui leads & vendas
  const perDay = useMemo(() => {
    type Row = { day: string; views: number; sessions: Set<string>; clicks: number; leads: number; sales: number; revenue: number };
    const map = new Map<string, Row>();
    const ensure = (d: string) => {
      const r = map.get(d) ?? { day: d, views: 0, sessions: new Set<string>(), clicks: 0, leads: 0, sales: 0, revenue: 0 };
      map.set(d, r);
      return r;
    };
    for (const v of views) {
      const r = ensure(new Date(v.created_at).toISOString().slice(0, 10));
      r.views += 1;
      r.sessions.add(v.session_id);
    }
    for (const c of clicks) ensure(new Date(c.created_at).toISOString().slice(0, 10)).clicks += 1;
    for (const l of leadsAll) ensure(new Date(l.created_at).toISOString().slice(0, 10)).leads += 1;
    for (const s of salesAll) {
      const dt = s.paid_at ?? s.created_at;
      const r = ensure(new Date(dt).toISOString().slice(0, 10));
      r.sales += 1;
      r.revenue += Number(s.revenue_value ?? s.gross_value ?? 0);
    }
    return Array.from(map.values())
      .sort((a, b) => a.day.localeCompare(b.day))
      .map((r) => ({
        day: r.day.slice(5),
        Sessões: r.sessions.size,
        Cliques: r.clicks,
        Leads: r.leads,
        Vendas: r.sales,
      }));
  }, [views, clicks, leadsAll, salesAll]);

  // Top 8 pages for bar chart
  const topPagesChart = useMemo(
    () =>
      perPage.slice(0, 8).map((p) => ({
        path: p.path.length > 22 ? "…" + p.path.slice(-21) : p.path,
        Sessões: p.sessions.size,
        Leads: p.leads,
        Vendas: p.sales,
      })),
    [perPage],
  );

  // Device breakdown: views por device
  const perDevice = useMemo(() => {
    const counts: Record<string, { device: string; views: number; sessions: Set<string>; clicks: number }> = {
      mobile: { device: "Mobile", views: 0, sessions: new Set(), clicks: 0 },
      desktop: { device: "Desktop", views: 0, sessions: new Set(), clicks: 0 },
      tablet: { device: "Tablet", views: 0, sessions: new Set(), clicks: 0 },
      desconhecido: { device: "Desconhecido", views: 0, sessions: new Set(), clicks: 0 },
    };
    for (const v of views) {
      const k = (v as { device?: string | null }).device || "desconhecido";
      const bucket = counts[k] ?? counts.desconhecido;
      bucket.views += 1;
      bucket.sessions.add(v.session_id);
    }
    for (const c of clicks) {
      const k = (c as { device?: string | null }).device || "desconhecido";
      const bucket = counts[k] ?? counts.desconhecido;
      bucket.clicks += 1;
    }
    return Object.values(counts)
      .filter((r) => r.views > 0 || r.clicks > 0)
      .map((r) => ({ device: r.device, Visualizações: r.views, Sessões: r.sessions.size, Cliques: r.clicks }));
  }, [views, clicks]);

  const fmtBRL = (v: number) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);
  const pct = (num: number, den: number) => (den > 0 ? ((num / den) * 100).toFixed(1) + "%" : "—");



  if (!authReady) return null;

  return (
    <div className="min-h-screen px-3 sm:px-4 py-6 sm:py-10">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8">
          <div className="min-w-0">
            <Button asChild variant="outline" size="sm" className="mb-3">
              <Link to="/admin"><ArrowLeft className="h-4 w-4 mr-1" /> Voltar ao menu</Link>
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground truncate">Dashboard de tráfego & conversão</h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">Sessões, cliques, leads e vendas reais por página no período.</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap sm:justify-end">
            <div className="flex-1 sm:flex-initial min-w-0">
              <PeriodFilter
                preset={period.preset}
                setPreset={period.setPreset}
                customFrom={period.customFrom}
                setCustomFrom={period.setCustomFrom}
                customTo={period.customTo}
                setCustomTo={period.setCustomTo}
              />
            </div>
            <Button variant="outline" size="icon" onClick={() => { refetch(); toast.success("Atualizando..."); }} disabled={isFetching}>
              <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>


        {/* KPIs principais — tráfego */}
        <div className="grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-3 mb-3">
          <Kpi label="Sessões únicas" value={uniqueSessions} loading={isLoading} />
          <Kpi label="Visualizações" value={totalViews} loading={isLoading} />
          <Kpi label="Cliques em CTAs" value={totalClicks} loading={isLoading} />
        </div>
        {/* KPIs de conversão */}
        <div className="grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-4 mb-8">
          <Kpi label="Leads capturados" value={totalLeads} loading={isLoading} hint={`${leadsHot} quentes · ${pct(totalLeads, uniqueSessions)} das sessões`} />
          <Kpi label="Vendas reais" value={totalSales} loading={isLoading} hint={`${pct(totalSales, totalLeads)} dos leads`} />
          <Kpi label="Faturamento" value={0} loading={isLoading} customValue={fmtBRL(totalRevenue)} hint="LP → checkout → compra" />
          <Kpi label="Ticket médio" value={0} loading={isLoading} customValue={fmtBRL(totalSales > 0 ? totalRevenue / totalSales : 0)} />
        </div>


        {seed && (seed.pageViews > 0 || seed.buttonClicks > 0 || seed.exampleReports > 0) && (
          <Card className="p-4 mb-6 border-amber-400/50 bg-amber-50 dark:bg-amber-950/30">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                  ⚠️ Modo demonstração — dados de teste no banco
                </p>
                <p className="text-xs text-amber-800 dark:text-admin-accent mt-1">
                  {seed.pageViews.toLocaleString("pt-BR")} sessões fake · {seed.buttonClicks.toLocaleString("pt-BR")} cliques fake · {seed.exampleReports} relatório(s) de exemplo.
                  Estes números <strong>não são reais</strong> — foram criados para apresentar o painel à equipe.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-amber-500/50 text-amber-900 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                onClick={async () => {
                  if (!confirm(`Excluir ${seed.pageViews.toLocaleString("pt-BR")} sessões, ${seed.buttonClicks.toLocaleString("pt-BR")} cliques e ${seed.exampleReports} relatório(s) de exemplo? Essa ação não pode ser desfeita.`)) return;
                  try {
                    const r = await deleteSeedFn();
                    toast.success(`Removidos: ${r.pageViews} sessões, ${r.buttonClicks} cliques, ${r.exampleReports} relatórios.`);
                    refetchSeed();
                    refetch();
                  } catch (e) {
                    toast.error(e instanceof Error ? e.message : "Falha ao limpar");
                  }
                }}
              >
                Limpar dados de teste
              </Button>
            </div>
          </Card>
        )}

        <AdsInvestimentoCard fromIso={fromIso} toIso={toIso} leads={leadsAll} sales={salesAll} />

        <Card className="p-0 overflow-hidden mb-8">
          <div className="px-5 py-4 border-b">
            <h2 className="font-semibold">Performance por página</h2>
            <p className="text-xs text-muted-foreground">Sessões → cliques → leads → vendas reais, por rota.</p>
          </div>
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Página</TableHead>
                <TableHead className="text-right">Sessões</TableHead>
                <TableHead className="text-right">Cliques</TableHead>
                <TableHead className="text-right">Leads</TableHead>
                <TableHead className="text-right">Conv. lead</TableHead>
                <TableHead className="text-right">Vendas</TableHead>
                <TableHead className="text-right">Faturam.</TableHead>
                <TableHead className="text-right">Conv. venda</TableHead>
                <TableHead>Botões</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {perPage.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-10">Sem dados no período.</TableCell></TableRow>
              ) : perPage.map((p) => (
                <TableRow key={p.path}>
                  <TableCell className="font-mono text-xs">{p.path}</TableCell>
                  <TableCell className="text-right font-medium">{p.sessions.size}</TableCell>
                  <TableCell className="text-right">{p.clicks}</TableCell>
                  <TableCell className="text-right">{p.leads > 0 ? <span className="font-medium">{p.leads}{p.leadsHot > 0 && <span className="text-[10px] text-rose-600 ml-1">🔥{p.leadsHot}</span>}</span> : <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground">{pct(p.leads, p.sessions.size)}</TableCell>
                  <TableCell className="text-right">{p.sales > 0 ? <span className="font-medium text-emerald-700">{p.sales}</span> : <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-right text-xs">{p.revenue > 0 ? fmtBRL(p.revenue) : <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground">{pct(p.sales, p.sessions.size)}</TableCell>
                  <TableCell>
                    {p.buttons.size === 0 ? <span className="text-xs text-muted-foreground">—</span> : (
                      <ul className="space-y-1">
                        {Array.from(p.buttons.values()).sort((a, b) => b.count - a.count).slice(0, 5).map((b, i) => (
                          <li key={i} className="text-xs">
                            <span className="font-medium">{b.label}</span>
                            <span className="text-muted-foreground"> · {b.count}×</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </Card>



        <Card className="p-0 overflow-hidden">
          <div className="px-5 py-4 border-b">
            <h2 className="font-semibold">Origem das sessões (UTMs)</h2>
            <p className="text-xs text-muted-foreground">Sessões com utm_source/medium/campaign capturados na entrada.</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Source</TableHead>
                <TableHead>Medium</TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead className="text-right">Sessões</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {utmRows.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-10">Nenhuma UTM no período.</TableCell></TableRow>
              ) : utmRows.map((r) => (
                <TableRow key={r.key}>
                  <TableCell className="font-mono text-xs">{r.source}</TableCell>
                  <TableCell className="font-mono text-xs">{r.medium}</TableCell>
                  <TableCell className="font-mono text-xs">{r.campaign}</TableCell>
                  <TableCell className="text-right font-medium">{r.sessions.size}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <div className="grid gap-6 lg:grid-cols-2 mt-8">
          <Card className="p-5">
            <h2 className="font-semibold mb-1">Evolução diária</h2>
            <p className="text-xs text-muted-foreground mb-4">Sessões, cliques, leads capturados e vendas reais por dia.</p>
            <div className="h-64">
              {perDay.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Sem dados no período.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={perDay} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="day" fontSize={11} />
                    <YAxis fontSize={11} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="Sessões" stroke="#3b82f6" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="Cliques" stroke="#10b981" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="Leads" stroke="#f59e0b" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="Vendas" stroke="#e11d48" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>

          <Card className="p-5">
            <h2 className="font-semibold mb-1">Top páginas — tráfego vs. conversão</h2>
            <p className="text-xs text-muted-foreground mb-4">Sessões, leads e vendas por rota (top 8).</p>
            <div className="h-64">
              {topPagesChart.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Sem dados no período.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topPagesChart} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="path" fontSize={10} interval={0} angle={-20} textAnchor="end" height={60} />
                    <YAxis fontSize={11} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Sessões" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Leads" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Vendas" fill="#e11d48" radius={[4, 4, 0, 0]} />

                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>
        </div>

        <Card className="p-5 mt-8">
          <h2 className="font-semibold mb-1">Acessos por dispositivo</h2>
          <p className="text-xs text-muted-foreground mb-4">Distribuição mobile / tablet / desktop no período. Útil pra orientar campanhas e revisar layout responsivo.</p>
          <div className="h-64">
            {perDevice.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Sem dados de dispositivo (coleta começou agora).</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={perDevice} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="device" fontSize={12} />
                  <YAxis fontSize={11} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Visualizações" fill="#f5b022" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Sessões" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Cliques" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Kpi({ label, value, loading, hint, customValue }: { label: string; value: number; loading: boolean; hint?: string; customValue?: string }) {
  return (
    <Card className="p-5">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-3xl font-bold mt-2">{loading ? "…" : (customValue ?? value.toLocaleString("pt-BR"))}</p>
      {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
    </Card>
  );
}
